import { useState, useEffect, useCallback } from 'react';
import {
  listRecurringPlans,
  createRecurringPlan,
  pauseRecurringPlan,
  resumeRecurringPlan,
  cancelRecurringPlan,
  updateRecurringPlan,
  getRecurringPlan,
} from '../lib/cerebro';

// ==================== CONSTANTS ====================

const ALLOWED_SYMBOLS = [
  'AAPL', 'MSFT', 'NVDA', 'TSLA', 'PLTR',
  'HIMS', 'DUOL', 'VOO', 'QQQ', 'BND',
  'GLD', 'SCHD', 'SOXX', 'ARKK', 'VWO',
];

const SYMBOL_NAMES = {
  AAPL: 'Apple', MSFT: 'Microsoft', NVDA: 'NVIDIA', TSLA: 'Tesla', PLTR: 'Palantir',
  HIMS: 'Hims & Hers', DUOL: 'Duolingo', VOO: 'S&P 500 ETF', QQQ: 'Nasdaq 100 ETF',
  BND: 'Bonos USA ETF', GLD: 'Oro ETF', SCHD: 'Dividendos ETF', SOXX: 'Semiconductores ETF',
  ARKK: 'Innovacion ETF', VWO: 'Emergentes ETF',
};

const SYMBOL_TYPES = {
  AAPL: 'accion', MSFT: 'accion', NVDA: 'accion', TSLA: 'accion', PLTR: 'accion',
  HIMS: 'accion', DUOL: 'accion', VOO: 'etf', QQQ: 'etf', BND: 'etf',
  GLD: 'etf', SCHD: 'etf', SOXX: 'etf', ARKK: 'etf', VWO: 'etf',
};

const FREQUENCIES = [
  { value: 'weekly', label: 'Semanal', desc: 'Cada semana', sublabel: '4x al mes' },
  { value: 'biweekly', label: 'Quincenal', desc: 'Cada 2 semanas', sublabel: '2x al mes' },
  { value: 'monthly', label: 'Mensual', desc: 'Una vez al mes', sublabel: '1x al mes' },
];

const DAY_NAMES = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes'];

const STATUS_CONFIG = {
  active: { label: 'Activo', color: '#10B981', bg: '#ECFDF5' },
  paused: { label: 'Pausado', color: '#F59E0B', bg: '#FFFBEB' },
  canceled: { label: 'Cancelado', color: '#6B7280', bg: '#F3F4F6' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', {
    day: 'numeric', month: 'short',
  });
}

// ==================== EXPLANATION SECTION ====================

function DcaExplanation({ onStart }) {
  return (
    <div>
      {/* Hero */}
      <div className="rounded-2xl p-6 mb-5 text-white" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #1a3a6c 100%)' }}>
        <div className="text-[10px] uppercase tracking-wider opacity-70 mb-2 font-semibold">
          Piloto Automatico
        </div>
        <h3 className="text-xl font-bold mb-2 leading-snug">
          Invierte automaticamente sin pensar en el momento perfecto
        </h3>
        <p className="text-sm opacity-90 leading-relaxed mb-4">
          Tu eliges cuanto y cada cuanto. Nosotros compramos por ti de forma automatica.
          Sin preocuparte por si el mercado esta alto o bajo.
        </p>
        <button
          onClick={onStart}
          className="px-6 py-3 bg-white text-[#0a2540] rounded-xl text-sm font-bold hover:bg-white/90 transition"
        >
          Crear mi primer plan
        </button>
      </div>

      {/* Como funciona */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
        <h4 className="text-sm font-bold text-[#181D27] mb-4">Como funciona</h4>
        <div className="space-y-4">
          <Step num="1" title="Elige que comprar" desc="Escoge una accion o ETF del catalogo. Puede ser Apple, el S&P 500, oro — lo que tu quieras." />
          <Step num="2" title="Define cuanto" desc="Desde $50,000 COP por compra. Si pones $50,000 semanales, son ~$200,000 al mes invirtiendo sin esfuerzo." />
          <Step num="3" title="Elige la frecuencia" desc="Semanal, quincenal o mensual. Cada ejecucion compra fracciones del activo con tu monto." />
          <Step num="4" title="Nosotros hacemos el resto" desc="Cada semana (o la frecuencia que elijas) compramos automaticamente. Tu portafolio crece solo." />
        </div>
      </div>

      {/* Por que funciona */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
        <h4 className="text-sm font-bold text-[#181D27] mb-3">Por que la inversion automatica funciona</h4>
        <p className="text-xs text-[#4B5563] leading-relaxed mb-4">
          Se llama Dollar Cost Averaging (DCA) y es la estrategia que usan los inversionistas mas
          exitosos del mundo. La idea es simple: en vez de tratar de adivinar cuando esta barato
          (nadie puede), compras un poco cada semana sin importar el precio.
        </p>
        <div className="space-y-2.5">
          <Benefit text="Cuando el precio esta alto, compras menos acciones" />
          <Benefit text="Cuando el precio esta bajo, compras mas acciones (esta en oferta)" />
          <Benefit text="Con el tiempo, tu precio promedio es mejor que el de la mayoria" />
          <Benefit text="Eliminas la emocion de la ecuacion — no vendes en panico, no compras en euforia" />
        </div>
      </div>

      {/* Ejemplo real */}
      <div className="bg-[#F0F4FF] rounded-2xl border border-[#D1DFFF] p-5 mb-4">
        <h4 className="text-sm font-bold text-[#181D27] mb-2">Ejemplo: $50,000 COP semanales en el S&P 500</h4>
        <p className="text-xs text-[#4B5563] leading-relaxed mb-3">
          Si hubieras puesto ~$50,000 COP cada semana en VOO (que sigue al S&P 500) durante los ultimos 10 anos,
          habrias invertido ~$26 millones COP y hoy tendrias ~$45 millones. Sin haber pensado un solo segundo en
          "el momento correcto".
        </p>
        <p className="text-[11px] text-[#6B7280] italic">
          Rendimientos historicos no garantizan resultados futuros. Pero la disciplina si.
        </p>
      </div>

      {/* FAQ rapido */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <h4 className="text-sm font-bold text-[#181D27] mb-3">Preguntas frecuentes</h4>
        <div className="space-y-3">
          <Faq q="Puedo pausar cuando quiera?" a="Si. Pausas y reanuudas con un click. Las acciones que ya compraste siguen siendo tuyas." />
          <Faq q="Cual es el monto minimo?" a="$50,000 COP (~$12 USD) por ejecucion. Es el mismo minimo de recarga de FlowexPay." />
          <Faq q="Cobra comision?" a="La misma comision de cualquier compra normal en FlowexPay. Sin cobros adicionales por automatizar." />
          <Faq q="Que pasa si no tengo saldo suficiente?" a="Si no hay saldo, esa ejecucion se salta sin cobrarte nada. Cuando recargues, la siguiente ejecucion programada se ejecuta normal." />
          <Faq q="Puedo tener varios planes a la vez?" a="Si, hasta 10 planes activos. Por ejemplo: $10 semanales en VOO + $5 mensuales en NVDA." />
        </div>
      </div>
    </div>
  );
}

function Step({ num, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full bg-[#2A59C6] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
        {num}
      </div>
      <div>
        <div className="text-sm font-semibold text-[#181D27]">{title}</div>
        <div className="text-xs text-[#6B7280] mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function Benefit({ text }) {
  return (
    <div className="flex gap-2 text-xs text-[#4B5563]">
      <span className="text-[#10B981] flex-shrink-0 mt-0.5">&#10003;</span>
      <span>{text}</span>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#F3F4F6] last:border-0 pb-2.5 last:pb-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-xs font-semibold text-[#181D27]">{q}</span>
        <span className="text-[#9CA3AF] text-sm ml-2 flex-shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="text-xs text-[#6B7280] mt-1.5 leading-relaxed">{a}</p>}
    </div>
  );
}

// ==================== CREATE PLAN FORM ====================

function CreatePlanForm({ onCreated, onCancel }) {
  const [step, setStep] = useState(1);
  const [symbol, setSymbol] = useState('');
  const [notional, setNotional] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(0); // 0=lun
  const [dayOfMonth, setDayOfMonth] = useState(15);
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('all'); // all | accion | etf

  const filteredSymbols = ALLOWED_SYMBOLS.filter((s) => {
    if (symbolFilter === 'all') return true;
    return SYMBOL_TYPES[s] === symbolFilter;
  });

  const canProceed = () => {
    if (step === 1) return !!symbol;
    if (step === 2) return notional && parseFloat(notional) >= 12;
    if (step === 3) return !!frequency;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const payload = {
      symbol,
      notional: parseFloat(notional),
      frequency,
      nickname: nickname.trim() || null,
    };
    if (frequency === 'monthly') {
      payload.dayOfMonth = dayOfMonth;
    } else {
      payload.dayOfWeek = dayOfWeek;
    }

    const result = await createRecurringPlan(payload);
    setLoading(false);

    if (result.success) {
      onCreated(result.data);
    } else {
      setError(result.message || 'Error al crear el plan.');
    }
  };

  // Estimacion mensual
  const amt = parseFloat(notional) || 0;
  const monthly = frequency === 'weekly' ? amt * 4 : frequency === 'biweekly' ? amt * 2 : amt;
  const yearly = monthly * 12;

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#F3F4F6]">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-[#181D27]">Nuevo plan automatico</h3>
          <button onClick={onCancel} className="text-[#9CA3AF] hover:text-[#6B7280] text-lg">&times;</button>
        </div>
        {/* Step indicator */}
        <div className="flex gap-1.5 mt-3">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-[#2A59C6]' : 'bg-[#E5E7EB]'}`} />
          ))}
        </div>
      </div>

      <div className="p-5">
        {/* STEP 1: Elegir simbolo */}
        {step === 1 && (
          <div>
            <h4 className="text-sm font-semibold text-[#181D27] mb-1">Que quieres comprar?</h4>
            <p className="text-xs text-[#6B7280] mb-4">
              Elige la accion o ETF que quieres comprar automaticamente.
              {!symbol && ' Si no sabes por donde empezar, VOO (S&P 500) es la opcion mas popular.'}
            </p>

            {/* Filter tabs */}
            <div className="flex gap-2 mb-3">
              {[
                { id: 'all', label: 'Todos' },
                { id: 'accion', label: 'Acciones' },
                { id: 'etf', label: 'ETFs' },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSymbolFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                    symbolFilter === f.id
                      ? 'bg-[#2A59C6] text-white'
                      : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {filteredSymbols.map((s) => (
                <button
                  key={s}
                  onClick={() => setSymbol(s)}
                  className={`p-2.5 rounded-xl border text-center transition ${
                    symbol === s
                      ? 'border-[#2A59C6] bg-[#F0F4FF] ring-1 ring-[#2A59C6]'
                      : 'border-[#E5E7EB] hover:border-[#2A59C6] hover:bg-[#FAFBFF]'
                  }`}
                >
                  <div className="text-sm font-bold text-[#2A59C6]">{s}</div>
                  <div className="text-[10px] text-[#6B7280] mt-0.5 leading-tight truncate">{SYMBOL_NAMES[s]}</div>
                </button>
              ))}
            </div>

            {symbol && (
              <div className="mt-4 p-3 bg-[#F0F4FF] rounded-xl">
                <div className="text-xs text-[#2A59C6] font-semibold">
                  Seleccionado: {symbol} — {SYMBOL_NAMES[symbol]}
                </div>
                <div className="text-[11px] text-[#6B7280] mt-0.5">
                  {SYMBOL_TYPES[symbol] === 'etf'
                    ? 'Los ETFs son fondos que contienen muchas acciones. Son ideales para diversificar automaticamente.'
                    : 'Las acciones te dan una participacion directa en la empresa.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Monto */}
        {step === 2 && (
          <div>
            <h4 className="text-sm font-semibold text-[#181D27] mb-1">Cuanto por compra?</h4>
            <p className="text-xs text-[#6B7280] mb-4">
              Cada vez que el Piloto Automatico se ejecute, comprara esta cantidad en {symbol}.
              Minimo $12 USD (~$50,000 COP), sin maximo.
            </p>

            <div className="relative mb-4">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-[#181D27]">$</span>
              <input
                type="number"
                min="12"
                step="1"
                value={notional}
                onChange={(e) => setNotional(e.target.value)}
                placeholder="12"
                className="w-full pl-10 pr-16 py-3.5 border border-[#E5E7EB] rounded-xl text-lg font-bold text-[#181D27] outline-none focus:border-[#2A59C6]"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#6B7280]">USD</span>
            </div>

            {/* Quick amounts */}
            <div className="flex gap-2 mb-4">
              {[12, 25, 50, 100, 250].map((a) => (
                <button
                  key={a}
                  onClick={() => setNotional(String(a))}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border ${
                    notional === String(a)
                      ? 'border-[#2A59C6] bg-[#F0F4FF] text-[#2A59C6]'
                      : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#2A59C6]'
                  }`}
                >
                  ${a}
                </button>
              ))}
            </div>

            {amt >= 12 && (
              <div className="text-xs text-[#6B7280] leading-relaxed">
                Compras fracciones de {symbol}. No necesitas el precio completo de la accion —
                con ${notional} USD compras la fraccion equivalente al momento de la ejecucion.
              </div>
            )}

            {amt > 0 && amt < 12 && (
              <div className="text-xs text-red-500">El monto minimo es $12 USD (~$50,000 COP) por ejecucion.</div>
            )}
          </div>
        )}

        {/* STEP 3: Frecuencia */}
        {step === 3 && (
          <div>
            <h4 className="text-sm font-semibold text-[#181D27] mb-1">Cada cuanto comprar?</h4>
            <p className="text-xs text-[#6B7280] mb-4">
              Mientras mas frecuente, mejor se distribuye tu inversion en el tiempo.
              La opcion mas popular es semanal.
            </p>

            <div className="space-y-2.5 mb-5">
              {FREQUENCIES.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFrequency(f.value)}
                  className={`w-full p-3.5 rounded-xl border text-left transition ${
                    frequency === f.value
                      ? 'border-[#2A59C6] bg-[#F0F4FF] ring-1 ring-[#2A59C6]'
                      : 'border-[#E5E7EB] hover:border-[#2A59C6]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#181D27]">{f.label}</div>
                      <div className="text-xs text-[#6B7280] mt-0.5">{f.desc}</div>
                    </div>
                    <div className="text-xs text-[#9CA3AF]">{f.sublabel}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Day picker */}
            {(frequency === 'weekly' || frequency === 'biweekly') && (
              <div>
                <div className="text-xs font-semibold text-[#181D27] mb-2">Dia preferido</div>
                <div className="flex gap-1.5">
                  {DAY_NAMES.map((d, i) => (
                    <button
                      key={i}
                      onClick={() => setDayOfWeek(i)}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold ${
                        dayOfWeek === i
                          ? 'bg-[#2A59C6] text-white'
                          : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
                      }`}
                    >
                      {d.slice(0, 3)}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#9CA3AF] mt-1.5">
                  Si el dia cae en fin de semana o festivo, se ejecuta el siguiente dia habil.
                </p>
              </div>
            )}

            {frequency === 'monthly' && (
              <div>
                <div className="text-xs font-semibold text-[#181D27] mb-2">Dia del mes</div>
                <div className="flex gap-2 flex-wrap">
                  {[1, 5, 10, 15, 20, 25].map((d) => (
                    <button
                      key={d}
                      onClick={() => setDayOfMonth(d)}
                      className={`w-12 py-2 rounded-lg text-xs font-semibold ${
                        dayOfMonth === d
                          ? 'bg-[#2A59C6] text-white'
                          : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#9CA3AF] mt-1.5">
                  Si el dia cae en fin de semana, se ejecuta el siguiente lunes.
                </p>
              </div>
            )}

            {/* Estimation */}
            {amt >= 5 && (
              <div className="mt-4 p-3 bg-[#F9FAFB] rounded-xl space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">Inversion mensual estimada</span>
                  <span className="font-semibold text-[#181D27]">${monthly.toFixed(0)} USD</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">Inversion anual estimada</span>
                  <span className="font-semibold text-[#181D27]">${yearly.toLocaleString()} USD</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 4: Confirmar */}
        {step === 4 && (
          <div>
            <h4 className="text-sm font-semibold text-[#181D27] mb-1">Confirma tu plan</h4>
            <p className="text-xs text-[#6B7280] mb-4">
              Revisa que todo este bien. Puedes pausar o cancelar en cualquier momento.
            </p>

            <div className="bg-[#F9FAFB] rounded-xl p-4 space-y-3 mb-4">
              <SummaryRow label="Activo" value={`${symbol} — ${SYMBOL_NAMES[symbol]}`} />
              <SummaryRow label="Monto por compra" value={`$${amt.toFixed(2)} USD`} />
              <SummaryRow
                label="Frecuencia"
                value={FREQUENCIES.find((f) => f.value === frequency)?.label}
              />
              {(frequency === 'weekly' || frequency === 'biweekly') && (
                <SummaryRow label="Dia" value={DAY_NAMES[dayOfWeek]} />
              )}
              {frequency === 'monthly' && (
                <SummaryRow label="Dia del mes" value={`Dia ${dayOfMonth}`} />
              )}
              <div className="border-t border-[#E5E7EB] pt-2">
                <SummaryRow label="Estimado mensual" value={`$${monthly.toFixed(0)} USD`} bold />
                <SummaryRow label="Estimado anual" value={`$${yearly.toLocaleString()} USD`} bold />
              </div>
            </div>

            {/* Optional nickname */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-[#181D27] block mb-1">
                Nombre del plan (opcional)
              </label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={`Mi plan ${SYMBOL_NAMES[symbol]}`}
                className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-xl text-sm outline-none focus:border-[#2A59C6]"
                maxLength={50}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 mb-4">
                {error}
              </div>
            )}

            <p className="text-[11px] text-[#9CA3AF] mb-4 leading-relaxed">
              Al activar el Piloto Automatico, autorizas compras automaticas por el monto indicado.
              Puedes pausar o cancelar cuando quieras. Las acciones compradas son tuyas y permanecen
              en tu portafolio aunque canceles el plan.
            </p>
          </div>
        )}
      </div>

      {/* Footer buttons */}
      <div className="px-5 py-4 border-t border-[#F3F4F6] flex gap-3">
        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="px-4 py-2.5 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB]"
          >
            Atras
          </button>
        )}
        <div className="flex-1" />
        {step < 4 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition ${
              canProceed()
                ? 'bg-[#2A59C6] text-white hover:bg-[#1E4BB8]'
                : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
            }`}
          >
            Continuar
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl text-sm font-bold bg-[#2A59C6] text-white hover:bg-[#1E4BB8] transition disabled:opacity-50"
          >
            {loading ? 'Creando...' : 'Activar Piloto Automatico'}
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-[#6B7280]">{label}</span>
      <span className={`text-[#181D27] ${bold ? 'font-bold' : 'font-semibold'}`}>{value}</span>
    </div>
  );
}

// ==================== PLAN CARD ====================

function PlanCard({ plan, onPause, onResume, onCancel, onDetail, actionLoading }) {
  const config = STATUS_CONFIG[plan.status] || STATUS_CONFIG.active;
  const freq = FREQUENCIES.find((f) => f.value === plan.frequency);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-3">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F0F4FF] flex items-center justify-center">
            <span className="text-sm font-bold text-[#2A59C6]">{plan.symbol.slice(0, 2)}</span>
          </div>
          <div>
            <div className="text-sm font-bold text-[#181D27]">
              {plan.nickname || `${plan.symbol} — ${SYMBOL_NAMES[plan.symbol] || ''}`}
            </div>
            <div className="text-xs text-[#6B7280]">
              ${plan.notional} USD · {freq?.label || plan.frequency}
            </div>
          </div>
        </div>
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ color: config.color, backgroundColor: config.bg }}
        >
          {config.label}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-[#F9FAFB] rounded-lg p-2 text-center">
          <div className="text-[10px] text-[#9CA3AF]">Invertido</div>
          <div className="text-xs font-bold text-[#181D27]">${(plan.totalInvestedUsd || 0).toFixed(2)}</div>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-2 text-center">
          <div className="text-[10px] text-[#9CA3AF]">Compras</div>
          <div className="text-xs font-bold text-[#181D27]">{plan.totalExecutions || 0}</div>
        </div>
        <div className="bg-[#F9FAFB] rounded-lg p-2 text-center">
          <div className="text-[10px] text-[#9CA3AF]">Proxima</div>
          <div className="text-xs font-bold text-[#181D27]">
            {plan.status === 'active' ? formatDateShort(plan.nextExecutionDate) : '—'}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onDetail(plan.id)}
          className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-xs font-semibold text-[#6B7280] hover:bg-[#F9FAFB]"
        >
          Detalle
        </button>
        {plan.status === 'active' && (
          <button
            onClick={() => onPause(plan.id)}
            disabled={actionLoading === plan.id}
            className="flex-1 py-2 border border-[#F59E0B] rounded-lg text-xs font-semibold text-[#F59E0B] hover:bg-[#FFFBEB] disabled:opacity-50"
          >
            {actionLoading === plan.id ? '...' : 'Pausar'}
          </button>
        )}
        {plan.status === 'paused' && (
          <>
            <button
              onClick={() => onResume(plan.id)}
              disabled={actionLoading === plan.id}
              className="flex-1 py-2 border border-[#10B981] rounded-lg text-xs font-semibold text-[#10B981] hover:bg-[#ECFDF5] disabled:opacity-50"
            >
              {actionLoading === plan.id ? '...' : 'Reanudar'}
            </button>
            <button
              onClick={() => onCancel(plan.id)}
              disabled={actionLoading === plan.id}
              className="py-2 px-3 border border-[#E5E7EB] rounded-lg text-xs font-semibold text-[#9CA3AF] hover:text-red-500 hover:border-red-300 disabled:opacity-50"
            >
              Cancelar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ==================== PLAN DETAIL ====================

function PlanDetail({ planId, onBack }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editNotional, setEditNotional] = useState('');
  const [editFrequency, setEditFrequency] = useState('');
  const [editError, setEditError] = useState('');

  const fetchPlan = useCallback(async () => {
    const result = await getRecurringPlan(planId);
    if (result.success) setPlan(result.data);
    setLoading(false);
  }, [planId]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const handlePause = async () => {
    setActionLoading(true);
    await pauseRecurringPlan(planId);
    await fetchPlan();
    setActionLoading(false);
  };

  const handleResume = async () => {
    setActionLoading(true);
    await resumeRecurringPlan(planId);
    await fetchPlan();
    setActionLoading(false);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    await cancelRecurringPlan(planId);
    await fetchPlan();
    setActionLoading(false);
  };

  const startEdit = () => {
    setEditNotional(String(plan.notional));
    setEditFrequency(plan.frequency);
    setEditError('');
    setEditing(true);
  };

  const saveEdit = async () => {
    const newNotional = parseFloat(editNotional);
    if (!Number.isFinite(newNotional) || newNotional < 5) {
      setEditError('Monto minimo $5 USD.');
      return;
    }
    setActionLoading(true);
    const updates = {};
    if (newNotional !== plan.notional) updates.notional = newNotional;
    if (editFrequency !== plan.frequency) updates.frequency = editFrequency;
    const result = await updateRecurringPlan(planId, updates);
    if (result.success) {
      setEditing(false);
      await fetchPlan();
    } else {
      setEditError(result.message);
    }
    setActionLoading(false);
  };

  if (loading) {
    return <div className="p-8 text-center text-sm text-[#9CA3AF]">Cargando plan...</div>;
  }

  if (!plan) {
    return (
      <div className="p-8 text-center">
        <div className="text-sm text-[#9CA3AF] mb-4">Plan no encontrado</div>
        <button onClick={onBack} className="text-sm text-[#2A59C6] font-semibold">Volver</button>
      </div>
    );
  }

  const config = STATUS_CONFIG[plan.status] || STATUS_CONFIG.active;
  const freq = FREQUENCIES.find((f) => f.value === plan.frequency);
  const history = plan.executionHistory || [];

  return (
    <div>
      {/* Header */}
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-[#2A59C6] hover:underline mb-4">
        &#8592; Mis planes
      </button>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-lg font-bold text-[#181D27]">{plan.symbol} — {SYMBOL_NAMES[plan.symbol] || ''}</div>
            {plan.nickname && <div className="text-xs text-[#6B7280]">{plan.nickname}</div>}
          </div>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ color: config.color, backgroundColor: config.bg }}
          >
            {config.label}
          </span>
        </div>

        {/* Info grid */}
        {!editing ? (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <InfoBox label="Monto por compra" value={`$${plan.notional} USD`} />
            <InfoBox label="Frecuencia" value={freq?.label || plan.frequency} />
            <InfoBox label="Total invertido" value={`$${(plan.totalInvestedUsd || 0).toFixed(2)} USD`} />
            <InfoBox label="Compras realizadas" value={plan.totalExecutions || 0} />
            <InfoBox label="Proxima compra" value={plan.status === 'active' ? formatDate(plan.nextExecutionDate) : '—'} />
            <InfoBox label="Ultima compra" value={plan.lastExecutedAt ? formatDate(plan.lastExecutedAt) : 'Ninguna aun'} />
            <InfoBox label="Creado" value={formatDate(plan.createdAt)} />
            {plan.totalSharesAcquired > 0 && (
              <InfoBox label="Acciones acumuladas" value={plan.totalSharesAcquired.toFixed(6)} />
            )}
          </div>
        ) : (
          <div className="mb-4 p-4 bg-[#F9FAFB] rounded-xl space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#181D27] block mb-1">Nuevo monto (USD)</label>
              <input
                type="number"
                min="5"
                value={editNotional}
                onChange={(e) => setEditNotional(e.target.value)}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm outline-none focus:border-[#2A59C6]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#181D27] block mb-1">Frecuencia</label>
              <select
                value={editFrequency}
                onChange={(e) => setEditFrequency(e.target.value)}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm outline-none focus:border-[#2A59C6] bg-white"
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            {editError && <div className="text-xs text-red-500">{editError}</div>}
            <div className="flex gap-2">
              <button
                onClick={saveEdit}
                disabled={actionLoading}
                className="px-4 py-2 bg-[#2A59C6] text-white rounded-lg text-xs font-semibold disabled:opacity-50"
              >
                {actionLoading ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-2 border border-[#E5E7EB] rounded-lg text-xs font-semibold text-[#6B7280]"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Pause warning */}
        {plan.status === 'paused' && plan.pausedReason === 'insufficient_funds' && (
          <div className="p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl text-xs text-[#92400E] mb-4">
            Plan pausado automaticamente por saldo insuficiente. Recarga tu cuenta y reanuda el plan.
          </div>
        )}
        {plan.status === 'paused' && plan.pausedReason === 'consecutive_failures' && (
          <div className="p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl text-xs text-[#92400E] mb-4">
            Plan pausado automaticamente despues de 3 intentos fallidos. Verifica tu saldo y reanuda el plan.
          </div>
        )}
        {plan.consecutiveFailures > 0 && plan.status === 'active' && (
          <div className="p-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl text-xs text-[#92400E] mb-4">
            {plan.consecutiveFailures} intento(s) fallido(s) consecutivo(s). Si llega a 3, el plan se pausa automaticamente.
          </div>
        )}

        {/* Action buttons */}
        {plan.status !== 'canceled' && (
          <div className="flex gap-2">
            {plan.status === 'active' && !editing && (
              <>
                <button
                  onClick={startEdit}
                  className="flex-1 py-2.5 border border-[#2A59C6] rounded-xl text-xs font-semibold text-[#2A59C6] hover:bg-[#F0F4FF]"
                >
                  Editar plan
                </button>
                <button
                  onClick={handlePause}
                  disabled={actionLoading}
                  className="flex-1 py-2.5 border border-[#F59E0B] rounded-xl text-xs font-semibold text-[#F59E0B] hover:bg-[#FFFBEB] disabled:opacity-50"
                >
                  {actionLoading ? '...' : 'Pausar'}
                </button>
              </>
            )}
            {plan.status === 'paused' && (
              <>
                <button
                  onClick={handleResume}
                  disabled={actionLoading}
                  className="flex-1 py-2.5 bg-[#10B981] text-white rounded-xl text-xs font-semibold hover:bg-[#059669] disabled:opacity-50"
                >
                  {actionLoading ? '...' : 'Reanudar plan'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="py-2.5 px-4 border border-[#E5E7EB] rounded-xl text-xs font-semibold text-[#9CA3AF] hover:text-red-500 hover:border-red-300 disabled:opacity-50"
                >
                  Cancelar plan
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Execution history */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <h4 className="text-sm font-bold text-[#181D27] mb-3">Historial de compras</h4>
          <div className="space-y-2">
            {history.slice().reverse().map((exec, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
                <div className="flex items-center gap-2.5">
                  <div className={`w-2 h-2 rounded-full ${exec.status === 'filled' ? 'bg-[#10B981]' : 'bg-red-400'}`} />
                  <div>
                    <div className="text-xs font-semibold text-[#181D27]">
                      {exec.status === 'filled' ? `$${exec.filledNotional?.toFixed(2) || exec.notional?.toFixed(2)}` : 'Fallida'}
                    </div>
                    <div className="text-[10px] text-[#9CA3AF]">{formatDate(exec.executedAt)}</div>
                  </div>
                </div>
                {exec.status === 'filled' && exec.filledQty && (
                  <div className="text-right">
                    <div className="text-xs font-semibold text-[#181D27]">{parseFloat(exec.filledQty).toFixed(6)} acc.</div>
                    {exec.filledAvgPrice && (
                      <div className="text-[10px] text-[#9CA3AF]">@ ${parseFloat(exec.filledAvgPrice).toFixed(2)}</div>
                    )}
                  </div>
                )}
                {exec.status !== 'filled' && exec.error && (
                  <div className="text-[10px] text-red-400 max-w-[120px] truncate">{exec.error}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length === 0 && (
        <div className="bg-[#F9FAFB] rounded-2xl p-5 text-center">
          <div className="text-sm text-[#9CA3AF]">Aun no hay compras. La primera sera el {formatDate(plan.nextExecutionDate)}.</div>
        </div>
      )}
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="bg-[#F9FAFB] rounded-lg p-2.5">
      <div className="text-[10px] text-[#9CA3AF]">{label}</div>
      <div className="text-xs font-bold text-[#181D27] mt-0.5">{value}</div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export default function PilotoAutomatico() {
  const [view, setView] = useState('loading'); // loading | empty | list | create | detail | success
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [successPlan, setSuccessPlan] = useState(null);

  const fetchPlans = useCallback(async () => {
    setView('loading');
    const result = await listRecurringPlans();
    if (result.success && result.data && result.data.length > 0) {
      setPlans(result.data);
      setView('list');
    } else {
      setPlans([]);
      setView('empty');
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handlePause = async (id) => {
    setActionLoading(id);
    await pauseRecurringPlan(id);
    await fetchPlans();
    setActionLoading(null);
  };

  const handleResume = async (id) => {
    setActionLoading(id);
    await resumeRecurringPlan(id);
    await fetchPlans();
    setActionLoading(null);
  };

  const handleCancel = async (id) => {
    setActionLoading(id);
    await cancelRecurringPlan(id);
    await fetchPlans();
    setActionLoading(null);
  };

  const handleCreated = (plan) => {
    setSuccessPlan(plan);
    setView('success');
  };

  // Success screen after creating a plan
  if (view === 'success' && successPlan) {
    const freq = FREQUENCIES.find((f) => f.value === successPlan.frequency);
    return (
      <div className="max-w-lg">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 text-center">
          <div className="w-16 h-16 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-[#10B981]">&#10003;</span>
          </div>
          <h3 className="text-lg font-bold text-[#181D27] mb-2">Piloto Automatico activado</h3>
          <p className="text-sm text-[#6B7280] mb-4">
            Cada {freq?.desc?.toLowerCase() || successPlan.frequency} compraremos ${successPlan.notional} USD
            en {successPlan.symbol} automaticamente.
          </p>
          <div className="bg-[#F9FAFB] rounded-xl p-3 mb-5 text-xs text-[#6B7280]">
            Primera compra programada: <strong className="text-[#181D27]">{formatDate(successPlan.nextExecutionDate)}</strong>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { setView('create'); }}
              className="flex-1 py-2.5 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB]"
            >
              Crear otro plan
            </button>
            <button
              onClick={() => fetchPlans()}
              className="flex-1 py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8]"
            >
              Ver mis planes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Detail view
  if (view === 'detail' && selectedPlanId) {
    return (
      <div className="max-w-lg">
        <PlanDetail
          planId={selectedPlanId}
          onBack={() => { setSelectedPlanId(null); fetchPlans(); }}
        />
      </div>
    );
  }

  // Create plan form
  if (view === 'create') {
    return (
      <div className="max-w-lg">
        <CreatePlanForm
          onCreated={handleCreated}
          onCancel={() => { if (plans.length > 0) setView('list'); else setView('empty'); }}
        />
      </div>
    );
  }

  // Loading
  if (view === 'loading') {
    return <div className="p-8 text-center text-sm text-[#9CA3AF]">Cargando planes...</div>;
  }

  // Empty state — show DCA explanation
  if (view === 'empty') {
    return (
      <div className="max-w-lg">
        <DcaExplanation onStart={() => setView('create')} />
      </div>
    );
  }

  // Plan list
  const activePlans = plans.filter((p) => p.status === 'active');
  const pausedPlans = plans.filter((p) => p.status === 'paused');
  const canceledPlans = plans.filter((p) => p.status === 'canceled');

  const totalMonthly = activePlans.reduce((sum, p) => {
    const mult = p.frequency === 'weekly' ? 4 : p.frequency === 'biweekly' ? 2 : 1;
    return sum + p.notional * mult;
  }, 0);

  return (
    <div className="max-w-lg">
      {/* Summary banner */}
      <div className="bg-gradient-to-r from-[#0a2540] to-[#1a3a6c] rounded-2xl p-5 mb-4 text-white">
        <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold mb-1">Piloto Automatico</div>
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-2xl font-bold">${totalMonthly.toFixed(0)}</span>
          <span className="text-sm opacity-70">USD / mes</span>
        </div>
        <div className="text-xs opacity-70">
          {activePlans.length} plan{activePlans.length !== 1 ? 'es' : ''} activo{activePlans.length !== 1 ? 's' : ''}
          {pausedPlans.length > 0 && ` · ${pausedPlans.length} pausado${pausedPlans.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Add plan button */}
      {activePlans.length < 10 && (
        <button
          onClick={() => setView('create')}
          className="w-full py-3 border-2 border-dashed border-[#D1DFFF] rounded-xl text-sm font-semibold text-[#2A59C6] hover:bg-[#F0F4FF] transition mb-4"
        >
          + Crear nuevo plan
        </button>
      )}

      {/* Active plans */}
      {activePlans.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Activos</h4>
          {activePlans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              onPause={handlePause}
              onResume={handleResume}
              onCancel={handleCancel}
              onDetail={(id) => { setSelectedPlanId(id); setView('detail'); }}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}

      {/* Paused plans */}
      {pausedPlans.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Pausados</h4>
          {pausedPlans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              onPause={handlePause}
              onResume={handleResume}
              onCancel={handleCancel}
              onDetail={(id) => { setSelectedPlanId(id); setView('detail'); }}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}

      {/* Canceled plans */}
      {canceledPlans.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Cancelados</h4>
          {canceledPlans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              onPause={handlePause}
              onResume={handleResume}
              onCancel={handleCancel}
              onDetail={(id) => { setSelectedPlanId(id); setView('detail'); }}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}
    </div>
  );
}
